import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Bug, Sparkles, Send } from 'lucide-react';

export default function SupportPanel() {
  const [activeTab, setActiveTab] = useState('bug');
  const [formData, setFormData] = useState({
    type: 'bug',
    priority: 'medium',
    subject: '',
    description: '',
    email: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // TODO: Implement email sending when SMTP is configured
    // For now, show success message
    
    toast.success(
      formData.type === 'bug' 
        ? 'Bug segnalato! Ti contatteremo presto.' 
        : 'Richiesta inviata! Ti ricontatteremo per discutere i dettagli.'
    );
    
    // Reset form
    setFormData({
      type: activeTab,
      priority: 'medium',
      subject: '',
      description: '',
      email: '',
      phone: ''
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Supporto & Richieste</h2>
        <p className="text-gray-600 mt-1">Segnala bug o richiedi personalizzazioni</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => {
            setActiveTab('bug');
            setFormData({...formData, type: 'bug'});
          }}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'bug'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Bug className="w-4 h-4 inline mr-2" />
          Segnala Bug
        </button>
        <button
          onClick={() => {
            setActiveTab('feature');
            setFormData({...formData, type: 'feature'});
          }}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'feature'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Sparkles className="w-4 h-4 inline mr-2" />
          Richiedi Personalizzazione
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {activeTab === 'bug' ? (
              <>
                <Bug className="w-6 h-6 inline mr-2 text-red-600" />
                Segnala un Bug o Problema
              </>
            ) : (
              <>
                <Sparkles className="w-6 h-6 inline mr-2 text-purple-600" />
                Richiedi Personalizzazione o Sviluppo Specifico
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeTab === 'bug' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900">
                <strong>💡 Aiutaci a migliorare!</strong><br/>
                Descrivi il problema nel dettaglio: cosa stavi facendo, cosa è successo, cosa ti aspettavi.
                Se possibile, allega screenshot.
              </p>
            </div>
          ) : (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-purple-900">
                <strong>✨ Personalizza ZonePass!</strong><br/>
                Hai bisogno di funzionalità specifiche? Integrazioni particolari? Layout personalizzato?
                Descrivi la tua esigenza e ti ricontatteremo con un preventivo.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Priority for bugs */}
            {activeTab === 'bug' && (
              <div>
                <Label htmlFor="priority">Priorità</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData({...formData, priority: value})}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Bassa - Problema minore</SelectItem>
                    <SelectItem value="medium">Media - Problema fastidioso</SelectItem>
                    <SelectItem value="high">Alta - Funzionalità bloccata</SelectItem>
                    <SelectItem value="critical">Critica - Impossibile usare l'app</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="subject">Oggetto *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                required
                placeholder={
                  activeTab === 'bug' 
                    ? "Es: Errore durante creazione evento" 
                    : "Es: Integrazione con Mailchimp"
                }
              />
            </div>

            <div>
              <Label htmlFor="description">
                Descrizione Dettagliata * 
                {activeTab === 'feature' && ' (includi budget indicativo se possibile)'}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                rows={8}
                placeholder={
                  activeTab === 'bug'
                    ? "1. Cosa stavi facendo\n2. Cosa è successo\n3. Cosa ti aspettavi\n4. Quando è successo (orario/data)\n5. Browser/dispositivo"
                    : "Descrivi nel dettaglio cosa vorresti:\n- Funzionalità richiesta\n- Casi d'uso\n- Integrazione necessaria\n- Timeline desiderata\n- Budget indicativo"
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email di Contatto *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="tua@email.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefono (opzionale)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+39 XXX XXX XXXX"
                />
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg">
              <Send className="w-4 h-4 mr-2" />
              {activeTab === 'bug' ? 'Invia Segnalazione' : 'Invia Richiesta'}
            </Button>

            <p className="text-xs text-center text-gray-500">
              Ti risponderemo entro 24-48 ore lavorative
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Bug className="w-5 h-5 text-blue-600" />
              Segnalazioni Bug
            </h3>
            <p className="text-sm text-gray-700">
              Gratuito - Risolviamo i bug nel più breve tempo possibile.
              I bug critici hanno priorità massima.
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Personalizzazioni
            </h3>
            <p className="text-sm text-gray-700">
              A pagamento - Preventivo personalizzato in base alla complessità.
              Tempi di sviluppo: 1-4 settimane.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
